// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/dashboard/hooks/useDashboardLayout.ts
// ════════════════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePermissoes } from '@/contexts/PermissoesContext';
import { FORMULARIOS } from '@/domain/formularios';
import { estoqueService } from '@/features/estoque/services/estoqueService';
import { dashboardService } from '../services/dashboardService';
import { CATALOGO_WIDGETS, LAYOUT_PADRAO, widgetPorId } from '../catalogo';
import type { DashboardLayoutData, WidgetPos } from '../types';

const QK_LAYOUT = ['dashboard-layout'];

/** Layout salvo do usuário (ou null → usar o padrão) + mutações. */
export function useDashboardLayout() {
  const qc = useQueryClient();
  const { temPermissao } = usePermissoes();

  const query = useQuery({
    queryKey: QK_LAYOUT,
    queryFn: dashboardService.getLayout,
    staleTime: 5 * 60_000,
  });

  const salvar = useMutation({
    mutationFn: (layout: DashboardLayoutData) => dashboardService.salvarMeuLayout(layout),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LAYOUT });
      toast.success('Dashboard salvo.');
    },
    onError: () => toast.error('Não foi possível salvar o dashboard.'),
  });

  const restaurar = useMutation({
    mutationFn: dashboardService.restaurarPadrao,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_LAYOUT });
      toast.success('Dashboard restaurado para o padrão.');
    },
    onError: () => toast.error('Não foi possível restaurar o padrão.'),
  });

  /** Widgets do layout resolvido, filtrados por catálogo + permissão de leitura. */
  const widgets = useMemo<WidgetPos[]>(() => {
    const salvo = query.data?.layout?.widgets;
    const base = salvo && salvo.length > 0 ? salvo : LAYOUT_PADRAO;
    return base.filter((pos) => {
      const def = widgetPorId(pos.id);
      if (!def) return false; // id desconhecido (widget removido do catálogo)
      return def.formularioId === null || temPermissao(def.formularioId, 'ler');
    });
  }, [query.data, temPermissao]);

  return {
    widgets,
    origem: query.data?.origem ?? 'padrao',
    carregando: query.isLoading,
    salvar,
    restaurar,
  };
}

/**
 * Ids de widgets que devem ficar OCULTOS no modo visualização (sem dado útil —
 * ex.: empresa de serviço sem estoque mínimo cadastrado). No modo edição todos
 * aparecem (como shell) para poderem ser reposicionados/removidos.
 *
 * Os hooks são chamados SEMPRE e na MESMA ordem (catálogo fixo) — as queries
 * compartilham queryKey com os componentes, então não há fetch duplicado.
 */
export function useWidgetsOcultos(): Set<string> {
  const { temPermissao } = usePermissoes();

  const podeVerReposicao = temPermissao(FORMULARIOS.ESTOQUE_REPOSICAO, 'ler');
  const reposicao = useQuery({
    queryKey: ['estoque-reposicao'],
    queryFn: estoqueService.getReposicao,
    enabled: podeVerReposicao,
    staleTime: 60_000,
  });

  const algumaAcaoRapida = [
    FORMULARIOS.DAV,
    FORMULARIOS.EMISSAO_NFE,
    FORMULARIOS.PRODUTOS,
    FORMULARIOS.PESSOAS_EMPRESAS,
  ].some((f) => temPermissao(f, 'ler') && temPermissao(f, 'incluir'));

  return useMemo(() => {
    const ocultos = new Set<string>();
    if (podeVerReposicao && reposicao.isSuccess && reposicao.data.length === 0)
      ocultos.add('estoque.alertas');
    if (!algumaAcaoRapida) ocultos.add('geral.acoes-rapidas');
    return ocultos;
  }, [podeVerReposicao, reposicao.isSuccess, reposicao.data, algumaAcaoRapida]);
}

export { CATALOGO_WIDGETS, LAYOUT_PADRAO, widgetPorId };
